import Konva from "konva";
import polylabel from "polylabel";
import {
  MapPermissionsType,
  PointType,
  PositionType,
} from "../types/seating-types";

type PolygonBoundsType = {
  width: number;
  height: number;
  x: number;
  y: number;
  cx: number;
  cy: number;
};

export default class SeatingUtilsFactory {
  static rotatePoint = ({ x, y }: PositionType, rad: number): PositionType => {
    return {
      x: x * Math.cos(rad) - y * Math.sin(rad),
      y: y * Math.cos(rad) + x * Math.sin(rad),
    };
  };

  /**
   * Returns polygon-related data
   */
  static getPolygonBounds = (points: PointType[]): PolygonBoundsType => {
    const xMap = points.map((x) => x[0]);
    const yMap = points.map((x) => x[1]);

    // absolute difference between x min and max
    const width = Math.abs(Math.min(...xMap)) + Math.abs(Math.max(...xMap));
    // absolute difference between y min and max
    const height = Math.abs(Math.max(...yMap) - Math.min(...yMap));

    const x = Math.min(...xMap);
    const y = Math.min(...yMap);

    const centroid = SeatingUtilsFactory.getPolygonCentroid(points);

    return { width, height, x, y, cx: centroid.x, cy: centroid.y };
  };

  static getPolygonCentroid = (points: PointType[]): PositionType => {
    // prevent similar polygons with 4 points from having labels in significantly different positions
    if (points.length <= 4) {
      const sum = points.reduce(
        (acc, curr) => ({
          x: acc.x + curr[0],
          y: acc.y + curr[1],
        }),
        { x: 0, y: 0 }
      );
      return {
        x: sum.x / points.length,
        y: sum.y / points.length,
      };
    }

    const polygon = [points];
    const centroid = polylabel(polygon, 1.0); // polylabel returns x,y in an array
    return {
      x: centroid[0],
      y: centroid[1],
    };
  };

  /*
   * Rotates label around the center of a segment
   */
  static rotateAroundCenter = (node: Konva.Node, rotation: number): void => {
    const topLeft = { x: -node.width() / 2, y: -node.height() / 2 };

    // TODO: Update Konva version. For now, set Konva as any type, since getAngle isn't part of the exported type.
    // Github thread: https://github.com/konvajs/konva/issues/1128
    const current = SeatingUtilsFactory.rotatePoint(
      topLeft,
      (Konva as any).getAngle(node.rotation()) // eslint-disable-line
    );
    const rotated = SeatingUtilsFactory.rotatePoint(
      topLeft,
      (Konva as any).getAngle(rotation) // eslint-disable-line
    );
    const dx = rotated.x - current.x;
    const dy = rotated.y - current.y;
    node.rotation(rotation);
    node.x(node.x() + dx);
    node.y(node.y() + dy);
  };

  /**
   * Returns the number of item types assigned to seat and location permissions
   */
  static getPermissionsAssignedCount = (
    itemTypeId: number,
    locationPermissions: MapPermissionsType[][],
    seatPermissions: MapPermissionsType[][]
  ): number => {
    let locationPermissionsUsed = 0;
    let seatPermissionsUsed = 0;

    const flatLocationPermissions = locationPermissions.flat();
    for (let i = 0; i < flatLocationPermissions.length; i += 1) {
      const permission = flatLocationPermissions[i];
      if (permission.seatingItemTypeId === itemTypeId) {
        locationPermissionsUsed += permission.capacity_limit;
      }
    }
    seatPermissionsUsed = seatPermissions
      .flat()
      .filter(
        (permission) => permission.seatingItemTypeId === itemTypeId
      ).length;

    return locationPermissionsUsed + seatPermissionsUsed;
  };
}
